import PropTypes from 'prop-types';
import React from 'react';
import {connect} from 'react-redux';
import {push} from 'connected-react-router';
import _ from 'underscore';

import common from '@material-ui/core/colors/common';
const white = common.white;
import CheckIcon from '@material-ui/icons/Check';

import {changeSetting} from 'common/actions/settings';
import {
  isNextPickup,
  isNextStop,
  interimStopCount,
  rideStatus,
  rideVehicleId,
} from 'common/types/Ride';
import {serviceTypes} from 'common/types/Service';
import {fetchAgenciesIfNeeded} from 'ondemand/actions/agency';
import {pushAlert, popAllAlerts} from 'ondemand/actions/alert';
import {cancelRide, clearCancelRideError, fetchRidesIfNeeded} from 'ondemand/actions/ride';
import {clearRideDetails, pollRideDetails, cancelRideDetails} from 'ondemand/actions/rideDetails';
import {fetchServicesIfNeeded} from 'ondemand/actions/service';
import {pollServiceEstimate, cancelServiceEstimate} from 'ondemand/actions/serviceEstimate';
import {pollVehiclePosition, cancelVehiclePosition} from 'ondemand/actions/vehiclePosition';
import RideMap from 'ondemand/components/RideMap';
import messages from 'ondemand/messages';
import {
  getActiveAgency,
  getActiveService,
  getActiveServiceLoad,
  getRideDetails,
  getRideError,
  getRides,
  getServiceLoadThresholds,
  getVehiclePosition,
} from 'ondemand/selectors';


const buildStopMessage = (stopIndex) => {
  const toBe = ['are', 'is'];
  const plural = ['s', ''];
  const index = stopIndex === 1 ? 1 : 0;
  return `There ${toBe[index]} ${stopIndex} stop${plural[index]} ahead of you`;
};

class RidePage extends React.Component {
  static displayName = 'RidePage';

  static propTypes = {
    activeService: PropTypes.object,
    activeServiceLoad: PropTypes.string,
    agency: PropTypes.object,
    dispatch: PropTypes.func.isRequired,
    error: PropTypes.string,
    rides: PropTypes.object,
    rideDetails: PropTypes.object,
    rideId: PropTypes.string,
    route: PropTypes.object,
    thresholds: PropTypes.object.isRequired,
    vehiclePosition: PropTypes.object,
  };

  componentDidMount() {
    this.props.dispatch(fetchRidesIfNeeded()).then(() => {
      const ride = this.props.rides[this.props.rideId];
      this.props.dispatch(changeSetting('agencyId', ride.agencyId));
      this.props.dispatch(changeSetting('serviceId', ride.serviceId));
      this.props.dispatch(fetchAgenciesIfNeeded()).then(() => {
        this.props.dispatch(fetchServicesIfNeeded(this.props.agency.agencyId)).then(() => {
          this.props.dispatch(pollRideDetails(this.props.agency.agencyId, this.props.rideId));
          if (
            (ride.status === rideStatus.PENDING || ride.status === rideStatus.IN_PROGRESS) &&
            this.props.activeService &&
            this.props.activeService.service_type !== serviceTypes.INADVANCE
          ) {
            this.props.dispatch(
              pollServiceEstimate(this.props.agency.agencyId, this.props.activeService.service_id)
            );
          } else {
            cancelServiceEstimate();
          }
        });
      });
    });
  }

  componentDidUpdate(prevProps) {
    const vehicleId = rideVehicleId(prevProps.rideDetails);
    const nextVehicleId = rideVehicleId(this.props.rideDetails);

    if (nextVehicleId && nextVehicleId !== vehicleId) {
      this.props.dispatch(pollVehiclePosition(this.props.agency.agencyId, nextVehicleId));
    } else if (nextVehicleId !== vehicleId) {
      this.props.dispatch(cancelVehiclePosition());
    }

    const hasStatus = (props, status) => props.rideDetails && props.rideDetails.status === status;

    const stopMessage = (props) => {
      if (!_.any([
        hasStatus(props, rideStatus.PENDING),
        hasStatus(props, rideStatus.IN_PROGRESS),
      ])) {
        return null;
      }
      if (isNextStop(props.rideDetails)) {
        return hasStatus(props, rideStatus.PENDING) ? messages.nextPickup : messages.nextDropoff;
      }
      const stopIndex = interimStopCount(props.rideDetails);
      if (stopIndex) {
        return buildStopMessage(stopIndex);
      }
      return null;
    };

    // When the ride has become pending.
    if (!hasStatus(prevProps, rideStatus.PENDING) && hasStatus(this.props, rideStatus.PENDING)) {
      this.props.dispatch(popAllAlerts());

      if (this.props.activeService && this.props.activeService.managed_mode) {
        this.props.dispatch(pushAlert({
          level: 'info',
          title: messages.rideApproved,
          hideClose: true,
        }));
      }

      if (this.props.rideDetails.origin.position && !isNextPickup(prevProps.rideDetails)) {
        this.props.dispatch(pushAlert({
          level: 'info',
          title: messages.walkToPickup,
          closeIcon: <CheckIcon color={white} />,
        }));
      }
    }

    const oldStopMessage = stopMessage(prevProps);
    const newStopMessage = stopMessage(this.props);

    if (newStopMessage !== null && newStopMessage !== oldStopMessage) {
      this.props.dispatch(popAllAlerts());
      this.props.dispatch(pushAlert({
        level: 'info',
        title: newStopMessage,
        hideClose: true,
      }));
    }

    // When the ride has started, or is otherwise no longer the
    // next pickup and there is no other message to show.
    if (!newStopMessage && _.any([
      _.all([
        isNextPickup(prevProps.rideDetails),
        !isNextPickup(this.props.rideDetails),
      ]),
      _.all([
        !hasStatus(prevProps, rideStatus.IN_PROGRESS),
        hasStatus(this.props, rideStatus.IN_PROGRESS),
      ]),
    ])) {
      // Pop alerts, to get rid of the nextPickup message.
      this.props.dispatch(popAllAlerts());
    }

    // When the ride has been completed.
    if (!hasStatus(prevProps, rideStatus.COMPLETE) && hasStatus(this.props, rideStatus.COMPLETE)) {
      // Pop alerts, to get rid of the nextDropoff message.
      this.props.dispatch(popAllAlerts());

      if (this.props.rideDetails.destination.position) {
        this.props.dispatch(pushAlert({
          level: 'info',
          title: messages.walkToDestination,
          closeIcon: <CheckIcon color={white} />,
        }));
      }
    }
  }

  componentWillUnmount() {
    this.props.dispatch(cancelVehiclePosition());
    this.props.dispatch(cancelServiceEstimate());
    this.props.dispatch(cancelRideDetails());
    this.props.dispatch(clearRideDetails());
    this.props.dispatch(popAllAlerts());
  }

  _handleErrorConfirm = () => {
    this.props.dispatch(clearCancelRideError());
  };

  _handleRideCancel = () => {
    this.props.dispatch(cancelRide(this.props.agency.agencyId, this.props.rideDetails.rideId))
      .then(() => this.props.dispatch(cancelVehiclePosition()))
      .then(() => this.props.dispatch(push('/')));
  };

  render() {
    if (!this.props.agency || !this.props.rideDetails) return null;
    return (
      <div style={{height: '100%'}}>
        <RideMap
          agency={this.props.agency}
          error={this.props.error}
          rideDetails={this.props.rideDetails}
          onErrorConfirm={this._handleErrorConfirm} onRideCancel={this._handleRideCancel}
          service={this.props.activeService}
          serviceLoad={this.props.activeServiceLoad}
          thresholds={this.props.thresholds}
          timezone={this.props.agency && this.props.agency.timezone}
          vehiclePosition={this.props.vehiclePosition}
        />
      </div>
    );
  }
}

const mapStateToProps = (state, ownProps) => ({
  activeService: getActiveService(state),
  activeServiceLoad: getActiveServiceLoad(state),
  agency: getActiveAgency(state),
  error: getRideError(state),
  rides: getRides(state),
  rideDetails: getRideDetails(state),
  rideId: ownProps.match.params.rideId,
  thresholds: getServiceLoadThresholds(),
  vehiclePosition: getVehiclePosition(state),
});

export default connect(mapStateToProps)(RidePage);



// WEBPACK FOOTER //
// ./src/ondemand/containers/RidePage.js